import { addDays, monthKey, startOfWeek, todayIso, addMonths, daysInMonth } from '../FieldOpsKit'
import { goalOf, paceFraction, salesOf, storeInfo } from '../store'

/**
 * Numbers behind the administrators' "Team summary" (people, routes, stores). Pure functions over the demo store; the
 * real backend will return the same shapes from queries over field_visits / field_deliveries / field_returns.
 */

export const PERIODS = [
    { key: 'today', label: 'Today' }, { key: 'week', label: 'This week' }, { key: 'month', label: 'This month' }, { key: 'last', label: 'Last month' },
]

/** { from, to, month, label } for a period key. `month` is the month whose goals apply. */
export const periodRange = (key) => {
    const today = todayIso()
    const cur = monthKey(today)
    if (key === 'today') return { from: today, to: today, month: cur, label: 'Today' }
    if (key === 'week') { const m = startOfWeek(today); return { from: m, to: addDays(m, 6), month: cur, label: 'This week' } }
    if (key === 'last') { const l = addMonths(cur, -1); return { from: l + '-01', to: l + '-' + String(daysInMonth(l)).padStart(2, '0'), month: l, label: 'Last month' } }
    return { from: cur + '-01', to: cur + '-' + String(daysInMonth(cur)).padStart(2, '0'), month: cur, label: 'This month' }
}

const countBy = (list, fn) => {
    const m = {}
    list.forEach((x) => { const k = fn(x); if (k) m[k] = (m[k] || 0) + 1 })
    return Object.keys(m).map((k) => ({ reason: k, n: m[k] })).sort((a, b) => b.n - a.n)
}

/** Visits of any filter inside a period, with the counters every report needs. */
export const visitBlock = (sales, filter, range) => {
    const today = todayIso()
    const list = sales.visits.filter((v) => v.date >= range.from && v.date <= range.to && v.status !== 'rescheduled' && (!filter || filter(v)))
    const done = list.filter((v) => v.status === 'done')
    const missed = list.filter((v) => v.status === 'missed' || (v.status === 'planned' && v.date < today))
    const orders = done.filter((v) => v.outcome === 'order')
    const withDur = done.filter((v) => v.dur > 0)
    return {
        list: list, planned: list.length, done: done.length, missed: missed.length, missedList: missed, upcoming: list.length - done.length - missed.length,
        due: done.length + missed.length, compliance: done.length + missed.length > 0 ? Math.round((done.length / (done.length + missed.length)) * 100) : null,
        orderVisits: orders.length, noOrder: done.filter((v) => v.outcome === 'no_order').length, closed: done.filter((v) => v.outcome === 'closed').length,
        orderRate: done.length ? Math.round((orders.length / done.length) * 100) : null,
        avgMin: withDur.length ? Math.round(withDur.reduce((a, v) => a + v.dur, 0) / withDur.length) : 0,
        far: done.filter((v) => v.far).length, manual: done.filter((v) => v.manual).length, extra: list.filter((v) => v.extra).length,
        noOrderReasons: countBy(done.filter((v) => v.outcome === 'no_order'), (v) => v.reason),
        missReasons: countBy(missed, (v) => v.missReason || 'No reason recorded'),
    }
}

/** Goal vs sales of a set of stores for a month (published goals only, like the salespeople see). */
export const goalBlock = (sales, stores, month) => {
    let goal = 0; let sold = 0; let met = 0; let behind = 0; let nogoal = 0
    stores.forEach((s) => {
        const g = goalOf(sales, month, s.id, true)
        const v = salesOf(sales, month, s.id)
        if (g) { goal += g; sold += v; if (v >= g) met++ } else nogoal++
    })
    return { goal: goal, sold: sold, pct: goal > 0 ? Math.round((sold / goal) * 100) : null, met: met, nogoal: nogoal, behind: behind, pace: paceFraction(month) }
}

const inRange = (iso, range) => iso >= range.from && iso <= range.to
const msRange = (range) => {
    const a = new Date(range.from + 'T00:00:00').getTime()
    const b = new Date(range.to + 'T23:59:59').getTime()
    return [a, b]
}

export const dayRecords = (field, actorKey, range) => Object.keys(field.days).filter((k) => k.indexOf(actorKey + '|') === 0 && inRange(k.split('|')[1], range)).map((k) => Object.assign({ date: k.split('|')[1] }, field.days[k]))

export const returnBlock = (field, filter, range) => {
    const [a, b] = msRange(range)
    const list = field.returns.filter((r) => r.at >= a && r.at <= b && (!filter || filter(r)))
    return { list: list, n: list.length, total: list.reduce((x, r) => x + r.total, 0), waiting: list.filter((r) => r.status === 'requested').length }
}

/** One salesperson in a period. */
export const repStats = (state, rep, range) => {
    const sales = state.sales
    const key = 'rep-' + rep
    const v = visitBlock(sales, (x) => x.rep === rep, range)
    const days = dayRecords(state.field, key, range)
    const stores = sales.stores.filter((s) => s.rep === rep)
    const g = goalBlock(sales, stores, range.month)
    const orders = sales.orders.filter((o) => o.rep === rep && inRange(o.date, range))
    return {
        key: key, rep: rep, name: 'Salesperson ' + rep, kind: 'rep', v: v, goal: g, stores: stores.length,
        orders: orders.length, ordersTotal: orders.reduce((a, o) => a + o.total, 0),
        miles: days.reduce((a, d) => a + (d.miles || 0), 0), daysWorked: days.length, unsent: days.filter((d) => !d.submitted && d.date < todayIso()).length,
        returns: returnBlock(state.field, (r) => r.actor === key, range),
    }
}

/** One driver in a period. */
export const driverStats = (state, driverId, range) => {
    const today = todayIso()
    const key = 'drv-' + driverId
    const drv = state.drivers.drivers.filter((d) => d.id === driverId)[0]
    const list = state.drivers.deliveries.filter((d) => d.driverId === driverId && inRange(d.date, range) && d.date <= today && d.status !== 'cancelled')
    const done = list.filter((d) => d.status === 'delivered')
    const onTime = done.filter((d) => d.deliveredOn <= d.date)
    const days = dayRecords(state.field, key, range)
    return {
        key: key, driverId: driverId, name: drv ? drv.name : driverId, kind: 'driver', total: list.length, done: done.length, onTime: onTime.length,
        onTimePct: done.length ? Math.round((onTime.length / done.length) * 100) : null, failed: list.filter((d) => d.events.some((e) => e.type === 'failed')).length,
        partial: done.filter((d) => d.shortLines && d.shortLines.length).length, left: list.length - done.length,
        cod: done.reduce((a, d) => a + (d.payment ? d.payment.amount : 0), 0), weight: done.reduce((a, d) => a + d.weightLb, 0),
        avgMin: done.filter((d) => d.arrivedAt && d.doneAt).length ? Math.round(done.filter((d) => d.arrivedAt && d.doneAt).reduce((a, d) => a + (d.doneAt - d.arrivedAt) / 60000, 0) / done.filter((d) => d.arrivedAt && d.doneAt).length) : 0,
        far: done.filter((d) => d.far).length,
        miles: days.reduce((a, d) => a + (d.miles || 0), 0), daysWorked: days.length, unsent: days.filter((d) => !d.submitted && d.date < today).length,
        returns: returnBlock(state.field, (r) => r.actor === key, range), deliveries: list,
    }
}

/** One route (all salespeople who work it). */
export const routeStats = (state, route, range) => {
    const stores = state.sales.stores.filter((s) => s.route === route)
    const ids = {}
    stores.forEach((s) => { ids[s.id] = true })
    const v = visitBlock(state.sales, (x) => !!ids[x.storeId], range)
    const g = goalBlock(state.sales, stores, range.month)
    const reps = Array.from(new Set(stores.map((s) => s.rep)))
    const sold = state.sales.orders.filter((o) => ids[o.storeId] && inRange(o.date, range)).reduce((a, o) => a + o.total, 0)
    return { route: route, stores: stores.length, reps: reps, v: v, goal: g, soldInPeriod: sold }
}

/** One row per store for the "Stores" view. */
export const storeRows = (state, range, vIdx, oIdx) => {
    const today = todayIso()
    return state.sales.stores.map((s) => {
        const info = storeInfo(state.sales, s.id, vIdx, oIdx)
        const vs = (vIdx[s.id] || []).filter((v) => v.date >= range.from && v.date <= range.to && v.status !== 'rescheduled')
        const done = vs.filter((v) => v.status === 'done').length
        const missed = vs.filter((v) => v.status === 'missed' || (v.status === 'planned' && v.date < today)).length
        const orders = (oIdx[s.id] || []).filter((o) => o.date >= range.from && o.date <= range.to)
        return { s: s, info: info, goal: range.month === monthKey(today) ? info.goal : goalOf(state.sales, range.month, s.id, true), sold: salesOf(state.sales, range.month, s.id), planned: vs.length, done: done, missed: missed, orders: orders.length, ordersTotal: orders.reduce((a, o) => a + o.total, 0) }
    })
}

/** Items that need someone's attention today, for the banner on top of the summary. */
export const attentionList = (state) => {
    const today = todayIso()
    const out = []
    const r = periodRange('month')
    ;['RT', 'JR', 'JC'].forEach((rep) => {
        const st = repStats(state, rep, r)
        if (st.v.compliance !== null && st.v.compliance < 80) out.push({ tone: 'amber', text: 'Salesperson ' + rep + ' is at ' + st.v.compliance + '% visit compliance this month.' })
        if (st.unsent > 0) out.push({ tone: 'amber', text: 'Salesperson ' + rep + ' has ' + st.unsent + ' day report' + (st.unsent === 1 ? '' : 's') + ' not sent.' })
    })
    state.drivers.drivers.forEach((d) => {
        const st = driverStats(state, d.id, r)
        if (st.unsent > 0) out.push({ tone: 'amber', text: d.name + ' has ' + st.unsent + ' day report' + (st.unsent === 1 ? '' : 's') + ' not sent.' })
        if (st.onTimePct !== null && st.onTimePct < 80) out.push({ tone: 'amber', text: d.name + ' delivered only ' + st.onTimePct + '% on time this month.' })
    })
    const delayed = state.drivers.deliveries.filter((d) => d.date < today && (d.status === 'planned' || d.status === 'out')).length
    if (delayed > 0) out.push({ tone: 'red', text: delayed + ' deliveries are delayed (past their date and not delivered).' })
    const waiting = state.field.returns.filter((x) => x.status === 'requested').length
    if (waiting > 0) out.push({ tone: 'blue', text: waiting + ' return request' + (waiting === 1 ? ' is' : 's are') + ' waiting for approval.' })
    return out
}
